import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useAuth0 } from '@auth0/auth0-react';
import { bookingAPI, userAPI, sessionBundleAPI, paymentAPI } from '../services/api';
import type { BulkBookingItem as ApiBulkBookingItem } from '@shared/api-client';
import { useI18n } from '../i18n/i18nContext';
import { getTranslatedField } from '../utils/translationUtils';
import { useUser } from '../contexts/UserContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useNoIndex } from '../hooks/useNoIndex';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { useBookingCartStore } from '../stores/bookingCartStore';
import { getErrorMessage } from '../utils/apiErrorHandler';
import { BookingCart } from '../components/BookingCart';
import {
  SubscriptionContract,
  SessionBundleContract,
  CouponPricing,
  Promotion,
} from '@shared/types';
import LocalizedLink from '../components/LocalizedLink';
import { useServiceMetaStore } from '../stores/serviceMetaStore';
import { pushToDataLayer, clearEcommerce } from '../utils/dataLayer';
import PhoneInput from '../components/PhoneInput';
import { formatPrice } from '@shared/utils';
import { formatInTimeZone as formatInTimeZoneHelper } from 'date-fns-tz';
import Giftcard from 'src/components/BookingCheckoutPage/Giftcard';
import CouponCode from 'src/components/BookingCheckoutPage/CouponCode';
import PromoCode from 'src/components/BookingCheckoutPage/PromoCode';

const TALLINN_TIMEZONE = 'Europe/Tallinn';
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface SessionBundleItem {
  id: string;
  name: string;
  name_en?: string;
  name_et?: string;
  name_ru?: string;
  name_fi?: string;
  type: 'bulk' | 'subscription';
}

interface PackagePackContract extends SessionBundleContract {
  packagePackID?: string;
  packageID?: string | null;
  type?: 'package_pack';
}

const PaymentForm: React.FC<{
  servicePrice: number;
  onPaymentMethodCreated: (paymentMethodId: string) => void;
  onPaymentError: (error: string) => void;
  triggerPayment: boolean;
  setTriggerPayment: (val: boolean) => void;
  name: string;
  email: string;
  onPaymentFocus?: () => void;
}> = ({
  servicePrice,
  onPaymentMethodCreated,
  onPaymentError,
  triggerPayment,
  setTriggerPayment,
  name,
  email,
  onPaymentFocus,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();

  useEffect(() => {
    if (triggerPayment && stripe && elements) {
      const createPaymentMethod = async () => {
        try {
          const card = elements.getElement(CardNumberElement);
          if (!card) {
            throw new Error('Card element not found');
          }

          const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: card,
            billing_details: {
              name: name,
              email: email,
            },
          });

          if (pmError) {
            throw new Error(pmError.message || 'Payment method creation failed');
          }

          if (!paymentMethod) {
            throw new Error('Payment method not created');
          }

          onPaymentMethodCreated(paymentMethod.id);
        } catch (err: any) {
          onPaymentError(err.message || 'Payment failed');
        } finally {
          setTriggerPayment(false);
        }
      };

      createPaymentMethod();
    }
  }, [
    triggerPayment,
    stripe,
    elements,
    onPaymentMethodCreated,
    onPaymentError,
    setTriggerPayment,
    name,
    email,
  ]);

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block font-medium text-gray-700">
          {t.checkout?.cardNumber || 'Card Number'}
        </label>
        <div className="rounded-md border border-gray-300 p-4">
          <CardNumberElement
            onFocus={onPaymentFocus}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#32325d',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="mb-2 block font-medium text-gray-700">
            {t.checkout?.expiryDate || 'Expiry Date'}
          </label>
          <div className="rounded-md border border-gray-300 p-4">
            <CardExpiryElement
              onFocus={onPaymentFocus}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#32325d',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
        <div>
          <label className="mb-2 block font-medium text-gray-700">{t.checkout?.cvc || 'CVC'}</label>
          <div className="rounded-md border border-gray-300 p-4">
            <CardCvcElement
              onFocus={onPaymentFocus}
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#32325d',
                    '::placeholder': {
                      color: '#aab7c4',
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingCheckoutPage: React.FC = () => {
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const { user: auth0User, isAuthenticated, loginWithRedirect } = useAuth0();
  const { t, language } = useI18n();
  useDocumentTitle(t.booking.confirmYourBooking);
  useNoIndex();
  const { user, isLoadingUser } = useUser();
  const { items: cartItems, clearCart } = useBookingCartStore();
  const { credits: serviceCredits } = useServiceMetaStore();

  const hasPackageItems = useMemo(
    () => cartItems.some((item) => item.type === 'package'),
    [cartItems]
  );
  const hasServiceItems = useMemo(
    () => cartItems.some((item) => item.type === 'service'),
    [cartItems]
  );
  const requiredPackageIds = useMemo(() => {
    return Array.from(
      new Set(cartItems.filter((item) => item.type === 'package').map((item) => item.id))
    );
  }, [cartItems]);
  const isMixedCart = hasPackageItems && hasServiceItems;
  const isPackageOnlyCart = hasPackageItems && !hasServiceItems;
  const canUseContractPayments = hasServiceItems && !hasPackageItems;

  const totalRequiredCredits = useMemo(() => {
    if (cartItems.length > 0) {
      return cartItems.reduce((sum, item) => {
        if (item.type === 'package') return sum + 1;

        const credits = serviceCredits[item.id] || 1;
        return sum + credits;
      }, 0);
    }
    return 0;
  }, [cartItems, serviceCredits]);

  // Phone number state - using full international format (e.g., "+3725123456")
  const [phoneValue, setPhoneValue] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  // Guest user states
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [guestCheckout, setGuestCheckout] = useState(false);

  // Gift card states
  const [giftCardCode, setGiftCardCode] = useState<string>('');
  const [giftCardBalance, setGiftCardBalance] = useState<number | null>(null);
  const [appliedGiftCard, setAppliedGiftCard] = useState(false);

  // Promo code states
  const [promoCode, setPromoCode] = useState<string>('');
  const [appliedPromoCode, setAppliedPromoCode] = useState<boolean>(false);
  const [promotion, setPromotion] = useState<Promotion | null>(null);

  // Coupon code states
  const [couponCode, setCouponCode] = useState<string>('');
  const [appliedCoupon, setAppliedCoupon] = useState<boolean>(false);
  const [couponPricing, setCouponPricing] = useState<CouponPricing | null>(null);

  // Subscription and session bundle contract states
  const [subscriptionContracts, setSubscriptionContracts] = useState<SubscriptionContract[]>([]);
  const [sessionBundleContracts, setSessionBundleContracts] = useState<SessionBundleContract[]>([]);
  const [packagePackContracts, setPackagePackContracts] = useState<PackagePackContract[]>([]);
  const [unavailableSubscriptions, setUnavailableSubscriptions] = useState<SubscriptionContract[]>(
    []
  );
  const [unavailableSessionBundles, setUnavailableSessionBundles] = useState<
    SessionBundleContract[]
  >([]);
  const [unavailablePackagePacks, setUnavailablePackagePacks] = useState<PackagePackContract[]>([]);
  const [selectedContract, setSelectedContract] = useState<string>('new_payment');
  const [allSessionBundles, setAllSessionBundles] = useState<SessionBundleItem[]>([]);

  // Payment states
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [triggerPayment, setTriggerPayment] = useState(false);
  const [isNavigatingToSuccess, setIsNavigatingToSuccess] = useState(false);

  const totalAvailableCredits = useMemo(() => {
    const subCredits = subscriptionContracts.reduce((sum, c) => {
      const remaining =
        c.sessionsRemaining ?? (c.limitations?.bookingsCount ?? 0) - (c.usages?.length || 0);
      return sum + remaining;
    }, 0);

    const sessionBundleCredits = sessionBundleContracts.reduce((sum, c) => {
      const remaining =
        c.sessionsRemaining ?? (c.limitations?.bookingsCount ?? 0) - (c.usages?.length || 0);
      return sum + remaining;
    }, 0);

    return subCredits + sessionBundleCredits;
  }, [subscriptionContracts, sessionBundleContracts]);

  const totalAvailablePackagePackCredits = useMemo(() => {
    return packagePackContracts.reduce((sum, c) => {
      const remaining =
        c.sessionsRemaining ?? (c.limitations?.bookingsCount ?? 0) - (c.usages?.length || 0);
      return sum + Math.max(0, remaining);
    }, 0);
  }, [packagePackContracts]);

  const isGlobalInsufficient =
    canUseContractPayments && totalAvailableCredits < totalRequiredCredits;
  const isPackagePackInsufficient =
    isPackageOnlyCart && totalAvailablePackagePackCredits < totalRequiredCredits;
  const hasTrackedBeginCheckout = useRef(false);
  const hasFiredPaymentInfo = useRef(false);

  // Redirect if no state and no cart items (but not when navigating to success)
  useEffect(() => {
    if (cartItems.length === 0 && !isSubmitting && !isNavigatingToSuccess) {
      navigate('/book');
    }
  }, [cartItems, navigate, isSubmitting, isNavigatingToSuccess]);

  useEffect(() => {
    if (cartItems.length > 0 && !hasTrackedBeginCheckout.current) {
      hasTrackedBeginCheckout.current = true;
      clearEcommerce();

      const items =
        cartItems.length > 0
          ? cartItems.map((item) => {
              const variant = formatInTimeZoneHelper(
                new Date(item.startTime),
                TALLINN_TIMEZONE,
                'dd_MM_yyyy_HH:mm'
              );
              return {
                item_id: item.id,
                item_name: item.name,
                item_category: item.type,
                item_variant: variant,
                location_id: 'Tallinn',
                price: item.price || 0,
                quantity: 1,
              };
            })
          : [];

      pushToDataLayer('begin_checkout', {
        content: { category: 'booking_service' },
        ecommerce: { items },
      });
    }
  }, [cartItems]);

  const trackPaymentInfo = (paymentType: string = 'pay_with_card') => {
    if (!hasFiredPaymentInfo.current && cartItems.length > 0) {
      hasFiredPaymentInfo.current = true;
      clearEcommerce();

      const items =
        cartItems.length > 0
          ? cartItems.map((item) => {
              const variant = formatInTimeZoneHelper(
                new Date(item.startTime),
                TALLINN_TIMEZONE,
                'dd_MM_yyyy_HH:mm'
              );
              return {
                item_id: item.id,
                item_name: item.name,
                item_category: item.type,
                item_variant: variant,
                location_id: 'Tallinn',
                price: item.price || 0,
                quantity: 1,
              };
            })
          : [];

      pushToDataLayer('add_payment_info', {
        content: { category: 'booking_service' },
        ecommerce: {
          payment_type: paymentType,
          items,
        },
      });
    }
  };

  // Fetch all available sessionBundles and subscriptions for name lookup
  useEffect(() => {
    const fetchAllSessionBundles = async () => {
      try {
        const response = await sessionBundleAPI.getAll();
        const sessionBundlesData = (response.data as any)?.data?.sessionBundles || response.data;
        const sessionBundlesArray = Array.isArray(sessionBundlesData) ? sessionBundlesData : [];
        setAllSessionBundles(sessionBundlesArray);
      } catch (err) {
        console.error('Error fetching all sessionBundles:', err);
      }
    };
    fetchAllSessionBundles();
  }, []);

  // Fetch user profile for phone number, name, and email
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isAuthenticated && !isLoadingUser) {
        try {
          const response = await userAPI.getProfile();

          // Initialize name and email from profile (preferred over Auth0)
          setName(response.data.name || '');
          setEmail(response.data.email || '');

          // Initialize phone from profile
          if (response.data.phoneNumber) {
            setPhoneValue(response.data.phoneNumber);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          // Fallback to Auth0 user if profile fetch fails
          if (auth0User) {
            setName(auth0User.name || '');
            setEmail(auth0User.email || '');
          }
        }
      }
    };

    fetchUserProfile();
  }, [isAuthenticated, isLoadingUser, auth0User]);

  // Load guest information from localStorage when component mounts
  useEffect(() => {
    if (!isAuthenticated) {
      const savedGuestName = localStorage.getItem('guest_name');
      const savedGuestEmail = localStorage.getItem('guest_email');
      const savedGuestPhone = localStorage.getItem('guest_phone');

      if (savedGuestName) {
        setGuestName(savedGuestName);
      }
      if (savedGuestEmail) {
        setGuestEmail(savedGuestEmail);
      }
      if (savedGuestPhone) {
        setPhoneValue(savedGuestPhone);
      }
    }
  }, [isAuthenticated]);

  // Fetch user's subscription and session bundle contracts
  useEffect(() => {
    const fetchContracts = async () => {
      if (isAuthenticated && !isLoadingUser && user) {
        try {
          // Fetch both subscriptions and sessionBundles in parallel
          const [subscriptionsRes, sessionBundlesRes, packagePacksRes] = await Promise.all([
            userAPI.getSubscriptions(),
            userAPI.getSessionBundles(),
            userAPI.getPackagePacks(),
          ]);

          const userSubscriptions = subscriptionsRes.data || [];
          const userSessionBundles = sessionBundlesRes.data || [];
          const userPackagePacks = packagePacksRes.data || [];

          // Filter available and unavailable contracts using the isAvailable flag from backend
          const activeSubscriptions = userSubscriptions.filter((contract) => {
            return (contract as any).isAvailable !== false;
          });

          const activeSessionBundles = userSessionBundles.filter((contract) => {
            return (contract as any).isAvailable !== false;
          });

          // Filter unavailable contracts (expired or no sessions remaining)
          const unavailableSubscriptionsList = userSubscriptions.filter((contract) => {
            return (contract as any).isAvailable === false;
          });

          const unavailableSessionBundlesList = userSessionBundles.filter((contract) => {
            return (contract as any).isAvailable === false;
          });

          const activePackagePacks = userPackagePacks.filter((contract: any) => {
            if (contract.isAvailable === false) return false;
            if (!isPackageOnlyCart) return true;

            if (!requiredPackageIds.length) return true;

            // If packageID is missing on older contracts, allow showing them as fallback.
            if (!contract.packageID) return true;
            return requiredPackageIds.includes(contract.packageID);
          });

          const unavailablePackagePacksList = userPackagePacks.filter((contract: any) => {
            const isUnavailable = contract.isAvailable === false;
            if (!isUnavailable) return false;

            if (!isPackageOnlyCart) return true;
            if (!requiredPackageIds.length) return true;
            if (!contract.packageID) return true;
            return requiredPackageIds.includes(contract.packageID);
          });

          setSubscriptionContracts(activeSubscriptions as any);
          setSessionBundleContracts(activeSessionBundles as any);
          setPackagePackContracts(activePackagePacks as any);
          setUnavailableSubscriptions(unavailableSubscriptionsList as any);
          setUnavailableSessionBundles(unavailableSessionBundlesList as any);
          setUnavailablePackagePacks(unavailablePackagePacksList as any);

          // Set default payment option to first available subscription/session bundle
          if (isPackageOnlyCart) {
            if (activePackagePacks.length > 0) {
              setSelectedContract(activePackagePacks[0].id);
            } else {
              setSelectedContract('new_payment');
            }
          } else if (hasPackageItems) {
            setSelectedContract('new_payment');
          } else if (activeSubscriptions.length > 0) {
            setSelectedContract(activeSubscriptions[0].id);
          } else if (activeSessionBundles.length > 0) {
            setSelectedContract(activeSessionBundles[0].id);
          }
        } catch (err) {
          console.error('Error fetching user contracts:', err);
        }
      }
    };

    fetchContracts();
  }, [
    isAuthenticated,
    isLoadingUser,
    user,
    hasPackageItems,
    isPackageOnlyCart,
    requiredPackageIds,
  ]);

  useEffect(() => {
    if (isMixedCart && selectedContract !== 'new_payment' && selectedContract !== 'pay_at_site') {
      setSelectedContract('new_payment');
    }
  }, [isMixedCart, selectedContract]);

  useEffect(() => {
    if (selectedContract === 'new_payment' || selectedContract === 'pay_at_site') return;

    if (isGlobalInsufficient || isPackagePackInsufficient) {
      setSelectedContract('new_payment');
    }
  }, [selectedContract, isGlobalInsufficient, isPackagePackInsufficient]);

  // Handle phone change for PhoneInput component
  const handlePhoneChange = (value: string) => {
    setPhoneValue(value);
    // Save full phone number to localStorage for guest users
    if (!isAuthenticated && value) {
      setTimeout(() => localStorage.setItem('guest_phone', value), 500);
    }
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Save guest information to localStorage
  const saveGuestInfoToStorage = (name: string, email: string) => {
    if (name.trim()) {
      localStorage.setItem('guest_name', name.trim());
    }
    if (email.trim()) {
      localStorage.setItem('guest_email', email.trim());
    }
  };

  const getValidationEmail = () => {
    return isAuthenticated ? email : guestEmail;
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(false);
    setCouponPricing(null);
  };

  useEffect(() => {
    if (hasPackageItems && appliedCoupon) {
      handleRemoveCoupon();
    }
  }, [hasPackageItems, appliedCoupon]);

  const handleRemoveGiftCard = () => {
    setGiftCardCode('');
    setGiftCardBalance(null);
    setAppliedGiftCard(false);
  };

  const handleRemovePromoCode = () => {
    setPromoCode('');
    setAppliedPromoCode(false);
    setPromotion(null);
  };

  // Clear guest information from localStorage
  const clearGuestInfoFromStorage = () => {
    localStorage.removeItem('guest_name');
    localStorage.removeItem('guest_email');
    localStorage.removeItem('guest_phone');
  };

  const validateGuestFields = () => {
    if (!isAuthenticated && guestCheckout) {
      // For guest users who chose to checkout as guest, validate name and email
      if (!guestName.trim()) {
        setError(t.booking.nameRequired);
        return false;
      }
      if (!guestEmail.trim()) {
        setEmailError(t.booking.emailRequired);
        return false;
      }
      if (!validateEmail(guestEmail)) {
        setEmailError(t.booking.emailInvalid);
        return false;
      }

      // Save guest information to localStorage for future use
      saveGuestInfoToStorage(guestName, guestEmail);
    }
    return true;
  };

  const handlePaymentMethodCreated = async (pmId: string) => {
    setPaymentError(null);

    if (cartItems.length === 0) return;

    try {
      const { finalPrice } = getPricingBreakdown();

      // If the adjusted price is 0 (e.g., fully covered by gift card or coupon), skip payment and proceed directly
      if (finalPrice <= 0) {
        await proceedWithBooking();
        return;
      }

      const existingPackageItem = cartItems.find((item) => item.type === 'package');

      const paymentIntentResponse = await paymentAPI.createBookingPaymentIntent({
        type: existingPackageItem ? 'package' : 'service',
        id: existingPackageItem ? existingPackageItem.id : cartItems[0].id,
        price: finalPrice,
      });

      const { clientSecret } = paymentIntentResponse.data as {
        clientSecret: string;
        paymentIntentId?: string;
      };

      // Confirm the payment using Stripe
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: pmId,
      });

      if (confirmError) {
        throw new Error(confirmError.message || 'Payment failed');
      }

      if (paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment was not successful');
      }

      // Payment succeeded, now proceed with booking
      await proceedWithBooking(paymentIntent.id);
    } catch (paymentErr: any) {
      console.error('Payment error:', paymentErr);
      setPaymentError(paymentErr.message || 'Payment failed');
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setPaymentError(error);
    setIsSubmitting(false);
  };

  const proceedWithBooking = async (piId?: string) => {
    try {
      const fullPhoneNumber = phoneValue;

      type CheckoutBulkBookingItem = Omit<ApiBulkBookingItem, 'date' | 'time' | 'phoneNumber'>;

      const allocatePriceAcrossServicesByCredits = (
        totalPrice: number,
        services: Array<{ serviceId: string }>
      ): number[] => {
        if (services.length <= 0) {
          return [];
        }

        const totalCents = Math.round((totalPrice || 0) * 100);
        const weights = services.map((service) =>
          Math.max(1, serviceCredits[service.serviceId] || 1)
        );
        const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

        if (totalWeight <= 0) {
          return Array.from({ length: services.length }, () => 0);
        }

        const rawAllocations = weights.map((weight) => (totalCents * weight) / totalWeight);
        const baseAllocations = rawAllocations.map((allocation) => Math.floor(allocation));
        let centsToDistribute =
          totalCents - baseAllocations.reduce((sum, allocation) => sum + allocation, 0);

        const remainderPriority = rawAllocations
          .map((allocation, index) => ({
            index,
            fractional: allocation - baseAllocations[index],
          }))
          .sort((a, b) => {
            if (b.fractional !== a.fractional) {
              return b.fractional - a.fractional;
            }
            return a.index - b.index;
          });

        for (let i = 0; i < remainderPriority.length && centsToDistribute > 0; i++) {
          baseAllocations[remainderPriority[i].index] += 1;
          centsToDistribute -= 1;
        }

        return baseAllocations.map((value) => {
          const allocatedCentsValue = Math.max(0, value);
          return allocatedCentsValue / 100;
        });
      };

      const flattenedItems: CheckoutBulkBookingItem[] =
        cartItems.length > 0
          ? cartItems.reduce<CheckoutBulkBookingItem[]>((acc, item) => {
              if (item.type === 'service') {
                acc.push({
                  cartItemId: item.cartItemId,
                  serviceId: item.id,
                  startTime: item.startTime,
                  duration: item.duration,
                  price: item.price,
                  // Include invitees if they exist for multi-participant bookings
                  ...(item.invitees && item.invitees.length > 0 ? { invitees: item.invitees } : {}),
                });

                return acc;
              }

              const packageGroupId = item.cartItemId;
              const allocatedServicePrices = allocatePriceAcrossServicesByCredits(
                item.price,
                item.services
              );

              item.services.forEach((service, index) => {
                acc.push({
                  cartItemId: `${item.cartItemId}:${service.serviceId}:${index}`,
                  serviceId: service.serviceId,
                  startTime: service.startTime,
                  duration: service.duration,
                  price: allocatedServicePrices[index] ?? 0,
                  isPackageService: true,
                  packageId: item.id,
                  packageName: item.name,
                  packageNameEn: item.nameEn,
                  packageGroupId,
                });
              });

              return acc;
            }, [])
          : [];

      const bulkBookingData = {
        items: flattenedItems,
        paymentDetails: piId ? { paymentIntentId: piId } : undefined,
        paymentMethod: selectedContract,
        selectedContractId:
          selectedContract === 'new_payment' || selectedContract === 'pay_at_site'
            ? undefined
            : selectedContract,
        giftCardCode: appliedGiftCard ? giftCardCode : undefined,
        couponCode: appliedCoupon ? couponCode : undefined,
        promoCode: appliedPromoCode ? promoCode : undefined,
        language,
        // Include guest information for guest checkout
        ...(guestCheckout
          ? {
              guestName: guestName,
              guestEmail: guestEmail,
              phoneNumber: fullPhoneNumber,
            }
          : isAuthenticated
            ? {
                phoneNumber: fullPhoneNumber,
              }
            : {}),
      };

      const response = await bookingAPI.createBulk(bulkBookingData);

      if (response.data) {
        clearCart();

        // Save phone number for future bookings
        if (isAuthenticated) {
          try {
            await userAPI.updateProfile({ phoneNumber: fullPhoneNumber });
          } catch (err) {
            console.error('Error saving phone number to profile:', err);
          }
          // Clear guest info from localStorage since user is now authenticated
          clearGuestInfoFromStorage();
        } else {
          localStorage.setItem('user_phone', fullPhoneNumber);
          // For successful guest bookings, keep the guest info for next time
          // (it was already saved in validateGuestFields)
        }

        // Navigate to success page
        const responseData = response.data as any;
        const bookingCount = Array.isArray(responseData)
          ? responseData.length
          : responseData.bookings?.length || 0;
        const orderId = responseData.orderId;

        // Set flag to prevent competing navigation
        setIsNavigatingToSuccess(true);

        if (orderId) {
          // Both authenticated and guest users with orderId can view booking details
          navigate(`/booking-success/${orderId}`);
        } else if (isAuthenticated) {
          navigate('/account', {
            state: {
              message:
                bookingCount > 1
                  ? t.booking.bulkBookingSuccess?.replace('{{count}}', bookingCount.toString()) ||
                    `Successfully created ${bookingCount} bookings`
                  : t.booking.success,
              type: 'success',
            },
          });
        } else {
          // Missing orderId in success response indicates an invalid backend response contract.
          setIsNavigatingToSuccess(false);
          setError(t.booking.bookingFailed);
          return;
        }
      }
    } catch (err: any) {
      console.error('Error creating booking:', err);

      if (err.response?.status === 409) {
        setError(
          getErrorMessage(
            err,
            t,
            'Some time slots are no longer available. Please go back and update your cart.'
          )
        );
      } else {
        setError(getErrorMessage(err, t, t.booking.bookingFailed));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setAttemptedSubmit(true);

    if (!isPhoneValid || !phoneValue) {
      return;
    }

    if (!validateGuestFields()) {
      return;
    }

    if (cartItems.length === 0) return;

    setIsSubmitting(true);
    setError(null);
    setPaymentError(null);

    const { finalPrice } = getPricingBreakdown();

    // If payment is required (new_payment selected and final price > 0), trigger payment form
    if (
      selectedContract === 'new_payment' &&
      (isAuthenticated || guestCheckout) &&
      finalPrice > 0
    ) {
      setTriggerPayment(true);
      return;
    }

    // Track payment info for subscription/session bundle credits if not already tracked
    if (selectedContract !== 'new_payment' && selectedContract !== 'pay_at_site') {
      const isSubscription = subscriptionContracts.some((c) => c.id === selectedContract);
      const isSessionBundle = sessionBundleContracts.some((c) => c.id === selectedContract);
      const isPackagePack = packagePackContracts.some((c) => c.id === selectedContract);
      trackPaymentInfo(
        isSubscription
          ? 'subscription_credits'
          : isSessionBundle
            ? 'session_bundle_credits'
            : isPackagePack
              ? 'package_pack_credits'
              : 'contract_credits'
      );
    }

    // Otherwise proceed directly with booking
    await proceedWithBooking();
  };

  if (cartItems.length === 0) {
    return null;
  }

  const getPricingBreakdown = () => {
    const totalPriceInCents = cartItems.reduce(
      (sum, item) => sum + Math.round((item.price || 0) * 100),
      0
    );
    const effectivePriceInCents =
      appliedCoupon && couponPricing
        ? Math.round((couponPricing.couponPrice || 0) * 100)
        : totalPriceInCents;

    const promoDiscountPct = Number(promotion?.service_discount_pct) || 0;
    const amountAfterPromoInCents =
      appliedPromoCode && promoDiscountPct > 0
        ? Math.max(0, Math.round((effectivePriceInCents * (100 - promoDiscountPct)) / 100))
        : effectivePriceInCents;

    const promoDiscountInCents = effectivePriceInCents - amountAfterPromoInCents;

    const giftCardBalanceInCents = giftCardBalance ? Math.round(giftCardBalance * 100) : 0;
    const giftCardDeductionInCents =
      appliedGiftCard && giftCardBalanceInCents > 0
        ? Math.min(giftCardBalanceInCents, amountAfterPromoInCents)
        : 0;

    const finalPriceInCents = amountAfterPromoInCents - giftCardDeductionInCents;

    return {
      totalPrice: totalPriceInCents / 100,
      effectivePrice: effectivePriceInCents / 100,
      amountAfterPromo: amountAfterPromoInCents / 100,
      promoDiscountAmount: promoDiscountInCents / 100,
      giftCardDeductionAmount: giftCardDeductionInCents / 100,
      finalPrice: finalPriceInCents / 100,
      remainingGiftCardBalance:
        Math.max(0, giftCardBalanceInCents - giftCardDeductionInCents) / 100,
    };
  };

  const pricingBreakdown = getPricingBreakdown();
  const { totalPrice, effectivePrice: effectiveFinalPrice, amountAfterPromo } = pricingBreakdown;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 pb-20 md:pb-8">
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-gray-900">
          {cartItems.length > 0 ? t.checkout?.title || 'Checkout' : t.booking.confirmYourBooking}
        </h1>
        <p className="text-lg text-gray-600">
          {cartItems.length > 0
            ? t.checkout?.bookingInfo || 'Review and confirm your bookings'
            : t.booking.confirmYourDetails}
        </p>
      </div>

      {cartItems.length > 0 ? (
        <BookingCart hideCheckoutButton={true} />
      ) : (
        <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
          <h3 className="mb-4 text-lg font-semibold">{t.booking.bookingDetails}</h3>
          <div className="space-y-3 text-gray-700">
            <div className="border-t pt-4">
              <div className="flex justify-between text-lg font-bold">
                <span>{t.checkout?.total || 'Total'}</span>
                <span>{formatPrice(totalPrice)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
        <h3 className="mb-4 text-lg font-semibold">{t.booking.contactInformation}</h3>
        <div className="space-y-4">
          {isAuthenticated ? (
            <>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {t.account.name} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  disabled
                  className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-gray-700"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  {t.account.phoneNumber} <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  value={phoneValue}
                  onChange={handlePhoneChange}
                  onValidation={setIsPhoneValid}
                  placeholder={t.account.phoneNumber}
                  required
                  error={attemptedSubmit && !phoneValue ? t.booking.phoneNumberRequired : undefined}
                />
                {!attemptedSubmit && (
                  <p className="mt-1 text-sm text-gray-500">{t.booking.phoneNumberRequired}</p>
                )}
              </div>
            </>
          ) : (
            <>
              {!guestCheckout ? (
                <div className="space-y-4">
                  <p className="text-gray-600">{t.booking.chooseHowToProceed}</p>
                  <div className="flex flex-col space-y-3">
                    <button
                      onClick={() => setGuestCheckout(true)}
                      className="rounded-md bg-teal-600 px-6 py-3 font-medium text-white hover:bg-teal-700"
                    >
                      {t.booking.continueAsGuest}
                    </button>
                    <button
                      onClick={() =>
                        loginWithRedirect({
                          appState: {
                            returnTo: window.location.pathname + window.location.search,
                            state: location.state,
                          },
                        })
                      }
                      className="rounded-md border border-gray-300 px-6 py-3 font-medium hover:bg-gray-50"
                    >
                      {t.booking.loginToBook}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {t.account.name} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={guestName}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setGuestName(newName);
                        // Save to localStorage as user types (with minimal debouncing)
                        if (newName.trim()) {
                          setTimeout(() => localStorage.setItem('guest_name', newName.trim()), 500);
                        }
                      }}
                      placeholder={t.booking.enterYourName}
                      className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 ${
                        attemptedSubmit && !guestName.trim()
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    {attemptedSubmit && !guestName.trim() && (
                      <p className="mt-1 text-sm text-red-600">{t.booking.nameRequired}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {t.account.email} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={guestEmail}
                      onChange={(e) => {
                        const newEmail = e.target.value;
                        setGuestEmail(newEmail);
                        setEmailError(null);
                        // Save to localStorage as user types (with minimal debouncing)
                        if (newEmail.trim() && validateEmail(newEmail)) {
                          setTimeout(
                            () => localStorage.setItem('guest_email', newEmail.trim()),
                            500
                          );
                        }
                      }}
                      placeholder={t.booking.enterYourEmail}
                      className={`w-full rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 ${
                        emailError || (attemptedSubmit && !guestEmail.trim())
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300'
                      }`}
                    />
                    {(emailError || (attemptedSubmit && !guestEmail.trim())) && (
                      <p className="mt-1 text-sm text-red-600">
                        {emailError || t.booking.emailRequired}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      {t.account.phoneNumber} <span className="text-red-500">*</span>
                    </label>
                    <PhoneInput
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      onValidation={setIsPhoneValid}
                      placeholder={t.account.phoneNumber}
                      required
                      error={
                        attemptedSubmit && !phoneValue ? t.booking.phoneNumberRequired : undefined
                      }
                    />
                    {!attemptedSubmit && (
                      <p className="mt-1 text-sm text-gray-500">{t.booking.phoneNumberRequired}</p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Subscription Contract Section - show for all authenticated users and guest checkout */}
      {(isAuthenticated || guestCheckout) && (
        <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
          <h3 className="mb-4 text-lg font-semibold">
            {t.booking.paymentOption || 'Payment Option'}
          </h3>
          <div className="space-y-3">
            {isMixedCart && (
              <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm text-amber-800">{t.booking.isMixedCart}</p>
              </div>
            )}
            {/* Global Insufficient Credits Warning */}
            {canUseContractPayments &&
              isGlobalInsufficient &&
              (subscriptionContracts.length > 0 || sessionBundleContracts.length > 0) && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
                  <p className="text-sm text-red-600">
                    {(
                      t.booking.insufficientCredits ||
                      "You have {{available}} sessions available, but you're trying to book {{required}} sessions."
                    )
                      .replace('{{available}}', totalAvailableCredits.toString())
                      .replace('{{required}}', totalRequiredCredits.toString())}
                  </p>
                  <div className="mt-1 text-sm text-red-600">
                    {t.booking.insufficientCreditsActionStart}
                    <LocalizedLink to={'/book'} className="underline hover:text-red-800">
                      {t.booking.insufficientCreditsActionModify || 'modify your booking'}
                    </LocalizedLink>
                    {t.booking.insufficientCreditsActionMiddle || ' or '}
                    <LocalizedLink to="/pricing" className="underline hover:text-red-800">
                      {t.booking.insufficientCreditsActionPurchase ||
                        'purchase an additional session bundle'}
                    </LocalizedLink>
                    {t.booking.insufficientCreditsActionEnd || '.'}
                  </div>
                </div>
              )}
            {/* Display Subscriptions */}
            {canUseContractPayments && subscriptionContracts.length > 0 && (
              <>
                <div className="mb-1 mt-3 font-semibold text-gray-700">
                  {t.booking.subscriptions || 'Subscriptions'}
                </div>
                {subscriptionContracts.map((contract) => {
                  const remainingSessions =
                    contract.sessionsRemaining ??
                    (contract.limitations?.bookingsCount ?? 0) - (contract.usages?.length || 0);
                  return (
                    <div key={contract.id} className={isGlobalInsufficient ? 'opacity-75' : ''}>
                      <label
                        className={
                          'flex items-center rounded-lg p-3 transition-colors ' +
                          (isGlobalInsufficient
                            ? 'cursor-not-allowed bg-gray-50'
                            : 'cursor-pointer hover:bg-gray-50')
                        }
                      >
                        <input
                          type="radio"
                          id={contract.id}
                          name="payment_option"
                          value={contract.id}
                          checked={selectedContract === contract.id}
                          onChange={(e) => {
                            setSelectedContract(e.target.value);
                            trackPaymentInfo('subscription_credits');
                            handleRemoveCoupon();
                            handleRemoveGiftCard();
                            handleRemovePromoCode();
                          }}
                          disabled={isGlobalInsufficient}
                          className="mr-3 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <span className="font-medium">
                            {(() => {
                              const sessionBundle = allSessionBundles.find(
                                (p) => p.id === contract.subscriptionID
                              );
                              return sessionBundle
                                ? getTranslatedField(sessionBundle, 'name', language)
                                : contract.name || 'Subscription';
                            })()}
                          </span>
                          <div className="text-sm text-gray-600">
                            {t.booking.remainingSessions || 'Remaining sessions'}:{' '}
                            {remainingSessions}
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
            {/* Display Session Bundles */}
            {canUseContractPayments && sessionBundleContracts.length > 0 && (
              <>
                <div className="mb-1 mt-3 font-semibold text-gray-700">
                  {t.booking.sessionBundles || 'Session Bundles'}
                </div>
                {sessionBundleContracts.map((contract) => {
                  const remainingSessions =
                    contract.sessionsRemaining ??
                    (contract.limitations?.bookingsCount ?? 0) - (contract.usages?.length || 0);
                  return (
                    <div key={contract.id} className={isGlobalInsufficient ? 'opacity-75' : ''}>
                      <label
                        className={
                          'flex items-center rounded-lg p-3 transition-colors ' +
                          (isGlobalInsufficient
                            ? 'cursor-not-allowed bg-gray-50'
                            : 'cursor-pointer hover:bg-gray-50')
                        }
                      >
                        <input
                          type="radio"
                          id={contract.id}
                          name="payment_option"
                          value={contract.id}
                          checked={selectedContract === contract.id}
                          onChange={(e) => {
                            setSelectedContract(e.target.value);
                            trackPaymentInfo('session_bundle_credits');

                            handleRemoveCoupon();
                            handleRemoveGiftCard();
                            handleRemovePromoCode();
                          }}
                          disabled={isGlobalInsufficient}
                          className="mr-3 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <span className="font-medium">
                            {(() => {
                              const sessionBundle = allSessionBundles.find(
                                (p) => p.id === contract.subscriptionID
                              );
                              return sessionBundle
                                ? getTranslatedField(sessionBundle, 'name', language)
                                : contract.name || 'Session Bundle';
                            })()}
                          </span>
                          <div className="text-sm text-gray-600">
                            {t.booking.remainingSessions || 'Remaining sessions'}:{' '}
                            {remainingSessions}
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
            {/* Display Package Packs for package-only carts */}
            {isPackageOnlyCart && packagePackContracts.length > 0 && (
              <>
                <div className="mb-1 mt-3 font-semibold text-gray-700">
                  {t.account?.myPackagePacks || 'Package Packs'}
                </div>
                {packagePackContracts.map((contract: any) => {
                  const remainingSessions =
                    contract.sessionsRemaining ??
                    (contract.limitations?.bookingsCount ?? 0) - (contract.usages?.length || 0);
                  return (
                    <div
                      key={contract.id}
                      className={isPackagePackInsufficient ? 'opacity-75' : ''}
                    >
                      <label
                        className={
                          'flex items-center rounded-lg p-3 transition-colors ' +
                          (isPackagePackInsufficient
                            ? 'cursor-not-allowed bg-gray-50'
                            : 'cursor-pointer hover:bg-gray-50')
                        }
                      >
                        <input
                          type="radio"
                          id={contract.id}
                          name="payment_option"
                          value={contract.id}
                          checked={selectedContract === contract.id}
                          onChange={(e) => {
                            setSelectedContract(e.target.value);
                            trackPaymentInfo('package_pack_credits');
                            handleRemoveCoupon();
                            handleRemoveGiftCard();
                            handleRemovePromoCode();
                          }}
                          disabled={isPackagePackInsufficient}
                          className="mr-3 text-teal-600 focus:ring-teal-500 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <span className="font-medium">{contract.name || 'Package Pack'}</span>
                          <div className="text-sm text-gray-600">
                            {t.booking.remainingSessions || 'Remaining sessions'}:{' '}
                            {remainingSessions}
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </>
            )}
            {/* Unavailable Subscriptions and Session Bundles Section */}
            {canUseContractPayments &&
              isAuthenticated &&
              (unavailableSubscriptions.length > 0 || unavailableSessionBundles.length > 0) && (
                <div className="mt-6 border-t border-gray-200 pt-6">
                  <h4 className="mb-2 font-semibold text-gray-700">
                    {t.booking?.unavailableTitle || 'Unavailable Subscriptions and Session Bundles'}
                  </h4>
                  <p className="mb-4 text-sm text-gray-600">
                    {t.booking?.unavailableMessage ||
                      'These subscriptions and session bundles have expired or have been fully used.'}
                  </p>
                  <div className="space-y-3 opacity-60">
                    {unavailableSubscriptions.map((contract) => {
                      const sessionBundle = allSessionBundles.find(
                        (p) => p.id === contract.subscriptionID
                      );
                      const translatedName = sessionBundle
                        ? getTranslatedField(sessionBundle, 'name', language)
                        : contract.name || 'Subscription';
                      const expirationDate =
                        (contract as any).expirationDate || (contract as any).ends_at;
                      const unavailableReason = (contract as any).unavailableReason;
                      return (
                        <div key={contract.id} className="rounded-lg border bg-gray-50 p-4">
                          <h5 className="font-semibold text-gray-700">{translatedName}</h5>
                          <p className="text-sm text-gray-500">Subscription</p>
                          {unavailableReason === 'expired' && expirationDate && (
                            <p className="text-sm text-gray-500">
                              {t.booking?.expired || 'Expired'}:{' '}
                              {new Date(expirationDate).toLocaleDateString(language, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          )}
                          {unavailableReason === 'no_sessions' && (
                            <p className="text-sm text-gray-500">All sessions used</p>
                          )}
                        </div>
                      );
                    })}
                    {unavailableSessionBundles.map((contract) => {
                      const sessionBundle = allSessionBundles.find(
                        (p) => p.id === contract.subscriptionID
                      );
                      const translatedName = sessionBundle
                        ? getTranslatedField(sessionBundle, 'name', language)
                        : contract.name || 'Session Bundle';
                      const expirationDate =
                        (contract as any).expirationDate || (contract as any).ends_at;
                      const unavailableReason = (contract as any).unavailableReason;
                      return (
                        <div key={contract.id} className="rounded-lg border bg-gray-50 p-4">
                          <h5 className="font-semibold text-gray-700">{translatedName}</h5>
                          <p className="text-sm text-gray-500">Session Bundle</p>
                          {unavailableReason === 'expired' && expirationDate && (
                            <p className="text-sm text-gray-500">
                              {t.booking?.expired || 'Expired'}:{' '}
                              {new Date(expirationDate).toLocaleDateString(language, {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          )}
                          {unavailableReason === 'no_sessions' && (
                            <p className="text-sm text-gray-500">All sessions used</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            {/* Unavailable Package Packs Section */}
            {isPackageOnlyCart && isAuthenticated && unavailablePackagePacks.length > 0 && (
              <div className="mt-6 border-t border-gray-200 pt-6">
                <h4 className="mb-2 font-semibold text-gray-700">
                  {t.account?.usedPackagePacksTitle || 'Unavailable Package Packs'}
                </h4>
                <p className="mb-4 text-sm text-gray-600">
                  {(t.account?.noActivePackagePacks as string) ||
                    'These package packs are expired or fully used.'}
                </p>
                <div className="space-y-3 opacity-60">
                  {unavailablePackagePacks.map((contract: any) => {
                    const expirationDate = contract.expirationDate || contract.ends_at;
                    const unavailableReason = contract.unavailableReason;
                    return (
                      <div key={contract.id} className="rounded-lg border bg-gray-50 p-4">
                        <h5 className="font-semibold text-gray-700">
                          {contract.name || 'Package Pack'}
                        </h5>
                        <p className="text-sm text-gray-500">Package Pack</p>
                        {unavailableReason === 'expired' && expirationDate && (
                          <p className="text-sm text-gray-500">
                            {t.booking?.expired || 'Expired'}:{' '}
                            {new Date(expirationDate).toLocaleDateString(language, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        )}
                        {unavailableReason === 'no_sessions' && (
                          <p className="text-sm text-gray-500">All sessions used</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Upsell message for users without sessionBundles or subscriptions */}
            {canUseContractPayments &&
              subscriptionContracts.length === 0 &&
              sessionBundleContracts.length === 0 && (
                <div className="mb-4 mt-4 rounded-md border border-blue-200 bg-blue-50 p-4 text-center">
                  <p className="text-sm text-blue-800">
                    {t.upsell?.message ||
                      "Booking individual sessions? It's cheaper to use a subscription or session bundle."}
                  </p>
                  <LocalizedLink
                    to="/pricing"
                    className="mt-2 inline-block text-sm font-semibold text-blue-600 hover:underline"
                  >
                    {t.upsell?.linkText || 'View Session Bundles & Memberships'}
                  </LocalizedLink>
                </div>
              )}
            {appliedGiftCard && giftCardBalance !== null && giftCardBalance >= amountAfterPromo ? (
              <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-4">
                <p className="font-medium text-green-800">
                  {t.booking.fullyCoveredByGiftCard ||
                    'Your booking is fully covered by the gift card!'}
                </p>
                <p className="mt-1 text-sm text-green-600">
                  {t.booking.noPaymentRequired || 'No additional payment is required.'}
                </p>
              </div>
            ) : (
              <>
                <div className="mb-1 mt-3 font-semibold text-gray-700">
                  {t.booking.paymentMethods}
                </div>
                <div key="new_payment">
                  <label className="flex cursor-pointer items-center rounded-lg p-3 transition-colors hover:bg-gray-50">
                    <input
                      type="radio"
                      id="new_payment"
                      radioGroup="payment_option"
                      name="payment_option"
                      value="new_payment"
                      checked={selectedContract === 'new_payment'}
                      onChange={(e) => setSelectedContract(e.target.value)}
                      className="mr-3 text-teal-600 focus:ring-teal-500"
                    />
                    <div>
                      <span className="font-medium">{t.booking.payByCard}</span>
                      {effectiveFinalPrice > 0 && (
                        <span className="ml-2 text-gray-600">
                          ({formatPrice(effectiveFinalPrice)})
                        </span>
                      )}
                    </div>
                  </label>
                </div>
                <div key="pay_at_site">
                  <label className="flex cursor-pointer items-center rounded-lg p-3 transition-colors hover:bg-gray-50">
                    <input
                      type="radio"
                      radioGroup="payment_option"
                      id="pay_at_site"
                      name="payment_option"
                      value="pay_at_site"
                      checked={selectedContract === 'pay_at_site'}
                      onChange={(e) => setSelectedContract(e.target.value)}
                      className="mr-3 text-teal-600 focus:ring-teal-500"
                    />
                    <div>
                      <span className="font-medium">{t.booking.payAtSite}</span>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Promo Code Section */}
      {(selectedContract === 'new_payment' || selectedContract === 'pay_at_site') &&
        !appliedCoupon && (
          <PromoCode
            promoCode={promoCode}
            setPromoCode={setPromoCode}
            appliedPromoCode={appliedPromoCode}
            setAppliedPromoCode={setAppliedPromoCode}
            promotion={promotion}
            setPromotion={setPromotion}
            onRemovePromoCode={handleRemovePromoCode}
            pricingBreakdown={pricingBreakdown}
          />
        )}

      {/* Coupon Code Section */}
      {(selectedContract === 'new_payment' || selectedContract === 'pay_at_site') &&
        !hasPackageItems &&
        !appliedGiftCard &&
        !appliedPromoCode && (
          <CouponCode
            couponCode={couponCode}
            setCouponCode={setCouponCode}
            appliedCoupon={appliedCoupon}
            setAppliedCoupon={setAppliedCoupon}
            couponPricing={couponPricing}
            setCouponPricing={setCouponPricing}
            getValidationEmail={getValidationEmail}
            onRemoveCoupon={handleRemoveCoupon}
            pricingBreakdown={pricingBreakdown}
          />
        )}

      {/* Gift Card Section */}
      {(selectedContract === 'new_payment' || selectedContract === 'pay_at_site') &&
        !appliedCoupon && (
          <Giftcard
            giftCardCode={giftCardCode}
            setGiftCardCode={setGiftCardCode}
            giftCardBalance={giftCardBalance}
            setGiftCardBalance={setGiftCardBalance}
            appliedGiftCard={appliedGiftCard}
            setAppliedGiftCard={setAppliedGiftCard}
            onRemoveGiftcard={handleRemoveGiftCard}
            pricingBreakdown={pricingBreakdown}
          />
        )}

      {/* Payment Details Section - Show when new_payment is selected and total price > 0 or coupon is applied */}
      {selectedContract === 'new_payment' &&
        (isAuthenticated || guestCheckout) &&
        effectiveFinalPrice > 0 && (
          <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
            <div className="mb-2 flex items-center">
              <svg
                className="mr-2 h-5 w-5 text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                  clipRule="evenodd"
                />
              </svg>
              <h3 className="text-lg font-semibold">
                {t.checkout?.paymentDetails || 'Payment Details'}
              </h3>
            </div>
            <p className="mb-4 text-sm text-gray-500">
              {t.checkout?.securePaymentInfo || 'Your payment is securely processed by Stripe.'}
            </p>

            <div className="mb-4 space-y-2">
              <p className="mb-2 text-sm text-gray-600">
                {t.checkout?.customerInfo || 'Customer Information'}:
              </p>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  {t.checkout?.name || 'Name'}
                </label>
                <input
                  type="text"
                  id="name"
                  value={guestCheckout ? guestName : name}
                  onChange={(e) => {
                    const newName = e.target.value;
                    if (guestCheckout) {
                      setGuestName(newName);
                      // Save to localStorage as user types (with minimal debouncing)
                      if (newName.trim()) {
                        setTimeout(() => localStorage.setItem('guest_name', newName.trim()), 500);
                      }
                    } else {
                      setName(newName);
                    }
                  }}
                  className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-green-500 focus:ring-green-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t.checkout?.email || 'Email'}
                </label>
                <input
                  type="email"
                  id="email"
                  value={guestCheckout ? guestEmail : email}
                  onChange={(e) => {
                    const newEmail = e.target.value;
                    if (guestCheckout) {
                      setGuestEmail(newEmail);
                      // Save to localStorage as user types (with minimal debouncing)
                      if (newEmail.trim() && validateEmail(newEmail)) {
                        setTimeout(() => localStorage.setItem('guest_email', newEmail.trim()), 500);
                      }
                    } else {
                      setEmail(newEmail);
                    }
                  }}
                  className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-green-500 focus:ring-green-500"
                  required
                />
              </div>
            </div>

            <Elements stripe={stripePromise}>
              <PaymentForm
                servicePrice={
                  appliedCoupon && couponPricing ? couponPricing.couponPrice : totalPrice
                }
                onPaymentMethodCreated={handlePaymentMethodCreated}
                onPaymentError={handlePaymentError}
                triggerPayment={triggerPayment}
                setTriggerPayment={setTriggerPayment}
                name={guestCheckout ? guestName : name}
                email={guestCheckout ? guestEmail : email}
                onPaymentFocus={() => trackPaymentInfo('pay_with_card')}
              />
            </Elements>
            {paymentError && (
              <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-600">{paymentError}</p>
              </div>
            )}
          </div>
        )}

      {error && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Cancellation Policy Notice */}
      <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4">
        <div className="flex">
          <svg className="mr-2 h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-sm text-amber-800">
            {t.account.cancellationPolicyNotice ||
              'Note: Bookings cannot be cancelled within 24 hours of the appointment time.'}
          </p>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={() => navigate('/book')}
          className="rounded-md border border-gray-300 px-6 py-2 text-gray-700 hover:bg-gray-50"
        >
          {t.common.back}
        </button>
        <button
          onClick={(e) => {
            if (
              isSubmitting ||
              !isPhoneValid ||
              !phoneValue ||
              (!isAuthenticated && !guestCheckout) ||
              (!isAuthenticated && guestCheckout && (!guestName.trim() || !guestEmail.trim()))
            ) {
              e.preventDefault();
              setAttemptedSubmit(true);
            } else {
              handleConfirm();
            }
          }}
          className={`rounded-md px-6 py-2 font-medium ${
            isSubmitting ||
            !isPhoneValid ||
            !phoneValue ||
            (!isAuthenticated && !guestCheckout) ||
            (!isAuthenticated && guestCheckout && (!guestName.trim() || !guestEmail.trim()))
              ? 'cursor-not-allowed bg-gray-300 text-gray-500'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isSubmitting
            ? t.booking.confirming
            : selectedContract !== 'new_payment' || effectiveFinalPrice <= 0
              ? cartItems.length > 1
                ? t.checkout?.confirmBookings || 'Confirm Bookings'
                : t.booking.confirmBooking
              : cartItems.length > 1
                ? t.checkout?.confirmBookings || 'Confirm Bookings'
                : t.booking.confirmAndPay}
        </button>
      </div>
    </div>
  );
};

export default BookingCheckoutPage;
